import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  GraduationCap, Target, Sparkles, TrendingUp, Plus, RefreshCw, BadgeCheck,
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from './ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from './ui/dialog';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from './ui/select';
import { EmptyState, CardSkeleton } from './common/States';
import { learningAPI, skillsAPI, okrAPI, performanceAPI } from '../services/api';
import { getErrorMessage } from '../lib/utils';

// The API stores proficiency as 1-5; these are the labels for those levels.
const PROFICIENCY = [
  { value: 1, label: 'Novice' },
  { value: 2, label: 'Beginner' },
  { value: 3, label: 'Intermediate' },
  { value: 4, label: 'Advanced' },
  { value: 5, label: 'Expert' },
];
const proficiencyLabel = (level) =>
  PROFICIENCY.find((p) => p.value === Number(level))?.label || '—';

const titleize = (s) => String(s || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const statusVariant = (s) => ({
  completed: 'default',
  in_progress: 'secondary',
  assigned: 'outline',
  on_track: 'default',
  at_risk: 'destructive',
  draft: 'outline',
}[s] || 'secondary');

/** Section shell: matches HROperationsModule so the two read as one product. */
const Section = ({ icon: Icon, title, description, action, loading, isEmpty, emptyText, children }) => (
  <Card>
    <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
      <div className="space-y-1">
        <CardTitle className="flex items-center gap-2 text-base">
          <Icon className="h-4 w-4 text-muted-foreground" />
          {title}
        </CardTitle>
        {description && <p className="text-sm text-muted-foreground">{description}</p>}
      </div>
      {action}
    </CardHeader>
    <CardContent>
      {loading && <CardSkeleton rows={4} />}
      {!loading && isEmpty && <EmptyState compact title={emptyText} />}
      {!loading && !isEmpty && children}
    </CardContent>
  </Card>
);

/**
 * Learning, skills, goals and the review in one place, because they are the
 * same question asked four ways: where am I, and what is next.
 *
 * Every list here is already scoped server-side — an employee's own records,
 * an admin's whole tenant — so nothing filters by role on the client.
 */
const GrowthModule = ({ currentTab = 'learning' }) => {
  const [loading, setLoading] = useState(true);
  const [courses, setCourses] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [records, setRecords] = useState([]);
  const [okrs, setOkrs] = useState([]);
  const [kpi, setKpi] = useState(null);
  const [skillDialog, setSkillDialog] = useState(false);
  const [skillForm, setSkillForm] = useState({
    skill_id: '', proficiency_level: '3', years_experience: 0, last_used: '',
  });

  const load = useCallback(async () => {
    setLoading(true);
    // allSettled: a tenant without the learning add-on still gets its skills
    // and OKRs rather than an empty screen.
    const results = await Promise.allSettled([
      learningAPI.listCourses(),
      learningAPI.listEnrollments(),
      skillsAPI.listCatalog(),
      skillsAPI.listRecords(),
      okrAPI.list(),
      performanceAPI.getMyKpi(),
    ]);
    const value = (r, fallback) => (r.status === 'fulfilled' ? r.value.data : fallback);
    setCourses(value(results[0], []));
    setEnrollments(value(results[1], []));
    setCatalog(value(results[2], []));
    setRecords(value(results[3], []));
    setOkrs(value(results[4], []));
    setKpi(value(results[5], null));
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const courseById = useMemo(
    () => Object.fromEntries(courses.map((c) => [c.id, c])),
    [courses],
  );
  const skillById = useMemo(
    () => Object.fromEntries(catalog.map((s) => [s.id, s])),
    [catalog],
  );

  const enroll = async (courseId) => {
    try {
      // employee_id omitted on purpose — the API reads it as "me".
      await learningAPI.upsertEnrollment({
        course_id: courseId,
        status: 'in_progress',
        progress_percent: 0,
      });
      toast.success('Enrolled');
      load();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Could not enrol'));
    }
  };

  const setProgress = async (enrollment, percent) => {
    try {
      await learningAPI.upsertEnrollment({
        course_id: enrollment.course_id,
        employee_id: enrollment.employee_id,
        status: percent >= 100 ? 'completed' : 'in_progress',
        progress_percent: percent,
        due_date: enrollment.due_date || null,
      });
      load();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Could not update progress'));
    }
  };

  const saveSkill = async () => {
    if (!skillForm.skill_id) {
      toast.error('Pick a skill');
      return;
    }
    try {
      await skillsAPI.upsertRecord({
        skill_id: Number(skillForm.skill_id),
        proficiency_level: Number(skillForm.proficiency_level),
        years_experience: Number(skillForm.years_experience) || 0,
        last_used: skillForm.last_used || null,
        verified: false,
      });
      toast.success('Skill saved');
      setSkillDialog(false);
      load();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Could not save skill'));
    }
  };

  const enrolledCourseIds = new Set(enrollments.map((e) => e.course_id));

  if (currentTab === 'learning') {
    return (
      <div className="space-y-6 animate-fade-in">
        <Section
          icon={GraduationCap}
          title="My learning"
          description="Courses assigned to you and the ones you have picked up."
          action={<Button variant="ghost" size="sm" onClick={load}><RefreshCw className="h-4 w-4" /></Button>}
          loading={loading}
          isEmpty={!enrollments.length}
          emptyText="No courses yet — enrol from the catalogue below."
        >
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Course</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="w-48">Progress</TableHead>
                <TableHead>Due</TableHead>
                <TableHead className="text-right">Update</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {enrollments.map((e) => (
                <TableRow key={e.id}>
                  <TableCell className="font-medium">
                    {courseById[e.course_id]?.title || `Course #${e.course_id}`}
                  </TableCell>
                  <TableCell><Badge variant={statusVariant(e.status)}>{titleize(e.status)}</Badge></TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Progress value={e.progress_percent || 0} className="h-2" />
                      <span className="text-xs text-muted-foreground w-10 text-right">
                        {Math.round(e.progress_percent || 0)}%
                      </span>
                    </div>
                  </TableCell>
                  <TableCell className="text-muted-foreground">{e.due_date || '—'}</TableCell>
                  <TableCell className="text-right">
                    <Select
                      value={String(Math.round(e.progress_percent || 0))}
                      onValueChange={(v) => setProgress(e, Number(v))}
                    >
                      <SelectTrigger className="w-24 ml-auto"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {[0, 25, 50, 75, 100].map((p) => (
                          <SelectItem key={p} value={String(p)}>{p}%</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Section>

        <Section
          icon={Sparkles}
          title="Course catalogue"
          description="Everything the organisation offers."
          loading={loading}
          isEmpty={!courses.length}
          emptyText="No courses published yet."
        >
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Course</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Mode</TableHead>
                <TableHead>Hours</TableHead>
                <TableHead className="text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {courses.map((c) => (
                <TableRow key={c.id}>
                  <TableCell>
                    <div className="font-medium">{c.title}</div>
                    {c.description && (
                      <div className="text-xs text-muted-foreground">{c.description}</div>
                    )}
                  </TableCell>
                  <TableCell className="text-muted-foreground">{c.category || '—'}</TableCell>
                  <TableCell className="text-muted-foreground">{titleize(c.delivery_mode)}</TableCell>
                  <TableCell className="text-muted-foreground">{c.duration_hours ?? '—'}</TableCell>
                  <TableCell className="text-right">
                    {enrolledCourseIds.has(c.id) ? (
                      <Badge variant="outline">Enrolled</Badge>
                    ) : (
                      <Button size="sm" variant="outline" onClick={() => enroll(c.id)}>Enrol</Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Section>
      </div>
    );
  }

  if (currentTab === 'skills') {
    return (
      <div className="space-y-6 animate-fade-in">
        <Section
          icon={BadgeCheck}
          title="My skills"
          description="Self-assessed. HR verifies — the tick is theirs to give, not yours."
          action={(
            <Button size="sm" onClick={() => setSkillDialog(true)}>
              <Plus className="h-4 w-4 mr-1" /> Add skill
            </Button>
          )}
          loading={loading}
          isEmpty={!records.length}
          emptyText="No skills recorded yet."
        >
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Skill</TableHead>
                <TableHead>Proficiency</TableHead>
                <TableHead>Years</TableHead>
                <TableHead>Last used</TableHead>
                <TableHead>Verified</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {records.map((r) => (
                <TableRow key={r.id}>
                  <TableCell className="font-medium">
                    {skillById[r.skill_id]?.name || `Skill #${r.skill_id}`}
                  </TableCell>
                  <TableCell>{proficiencyLabel(r.proficiency_level)}</TableCell>
                  <TableCell className="text-muted-foreground">{r.years_experience ?? '—'}</TableCell>
                  <TableCell className="text-muted-foreground">{r.last_used || '—'}</TableCell>
                  <TableCell>
                    {r.verified
                      ? <Badge variant="default">Verified</Badge>
                      : <span className="text-xs text-muted-foreground">Awaiting HR</span>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Section>

        <Dialog open={skillDialog} onOpenChange={setSkillDialog}>
          <DialogContent>
            <DialogHeader><DialogTitle>Add a skill</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Skill</Label>
                <Select
                  value={skillForm.skill_id}
                  onValueChange={(v) => setSkillForm({ ...skillForm, skill_id: v })}
                >
                  <SelectTrigger><SelectValue placeholder="Select a skill" /></SelectTrigger>
                  <SelectContent>
                    {catalog.map((s) => (
                      <SelectItem key={s.id} value={String(s.id)}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Proficiency</Label>
                <Select
                  value={skillForm.proficiency_level}
                  onValueChange={(v) => setSkillForm({ ...skillForm, proficiency_level: v })}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PROFICIENCY.map((p) => (
                      <SelectItem key={p.value} value={String(p.value)}>{p.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Years of experience</Label>
                  <Input
                    type="number" min="0" step="0.5"
                    value={skillForm.years_experience}
                    onChange={(e) => setSkillForm({ ...skillForm, years_experience: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Last used</Label>
                  <Input
                    type="date"
                    value={skillForm.last_used}
                    onChange={(e) => setSkillForm({ ...skillForm, last_used: e.target.value })}
                  />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setSkillDialog(false)}>Cancel</Button>
              <Button onClick={saveSkill}>Save</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  if (currentTab === 'okrs') {
    return (
      <div className="space-y-6 animate-fade-in">
        <Section
          icon={Target}
          title="Goals & OKRs"
          description="Your objectives and the key results that move them."
          action={<Button variant="ghost" size="sm" onClick={load}><RefreshCw className="h-4 w-4" /></Button>}
          loading={loading}
          isEmpty={!okrs.length}
          emptyText="No objectives set for you yet."
        >
          <div className="space-y-4">
            {okrs.map((o) => (
              <div key={o.id} className="rounded-lg border border-border p-4 space-y-3">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="font-medium">{o.title}</div>
                    {o.description && (
                      <div className="text-sm text-muted-foreground">{o.description}</div>
                    )}
                  </div>
                  <Badge variant={statusVariant(o.status)}>{titleize(o.status)}</Badge>
                </div>
                <div className="flex items-center gap-3">
                  <Progress value={o.progress_percent || 0} className="h-2" />
                  <span className="text-xs text-muted-foreground w-10 text-right">
                    {Math.round(o.progress_percent || 0)}%
                  </span>
                </div>
                {!!(o.key_results || []).length && (
                  <div className="space-y-1 pt-1">
                    {o.key_results.map((kr) => (
                      <div key={kr.id} className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">{kr.title}</span>
                        <span className="tabular-nums">
                          {kr.current_value ?? 0} / {kr.target_value ?? 0} {kr.metric_unit || ''}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </Section>
      </div>
    );
  }

  // review
  return (
    <div className="space-y-6 animate-fade-in">
      <Section
        icon={TrendingUp}
        title="My performance"
        description="This month's scorecard, as your manager sees it."
        action={<Button variant="ghost" size="sm" onClick={load}><RefreshCw className="h-4 w-4" /></Button>}
        loading={loading}
        isEmpty={!kpi}
        emptyText="No scorecard published for you yet."
      >
        {kpi && (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {[
              ['Overall score', kpi.overall_score],
              ['Goal achievement', kpi.goal_achievement],
              ['Attendance', kpi.attendance_score],
              ['Productivity', kpi.productivity_score],
            ].filter(([, v]) => v !== undefined && v !== null).map(([label, value]) => (
              <div key={label} className="rounded-lg border border-border p-4">
                <div className="text-xs text-muted-foreground">{label}</div>
                <div className="text-2xl font-semibold tabular-nums">
                  {Number(value).toFixed(1)}
                </div>
              </div>
            ))}
          </div>
        )}
      </Section>
    </div>
  );
};

export default GrowthModule;
